import { useCallback, useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

export type RestoreState<T> = (storedState: Partial<T>, initialState: T) => T;

export type StorageType = 'local' | 'session';

/** `undefined` without a window or when the browser blocks storage access. */
function webStorage(type: StorageType): Storage | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    return type === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return undefined;
  }
}

function readState<T>(
  storageKey: string | undefined,
  storageType: StorageType,
  initialState: T,
  restore: RestoreState<T>,
): T {
  const storage = webStorage(storageType);
  if (!storageKey || !storage) return initialState;
  try {
    const storedState = storage.getItem(storageKey);
    if (!storedState) return initialState;
    return restore(JSON.parse(storedState) as Partial<T>, initialState);
  } catch (error) {
    try {
      storage.removeItem(storageKey);
    } catch {
      // Storage is blocked; the initial state is all there is.
    }
    console.error(
      `Couldn't load ${storageKey} state from web storage: `,
      error,
    );
    return initialState;
  }
}

/**
 * `useState` mirrored to web storage under `storageKey`. Without a key it is a
 * plain `useState`. When the key changes, the state is read again from the new
 * key, so one entry never overwrites another. `restore` merges what was stored
 * into the initial value and should check each field, since stored data may
 * come from an older version.
 */
export function usePersistentState<T>(
  storageKey: string | undefined,
  initialState: T,
  restore: RestoreState<T>,
  storageType: StorageType = 'local',
): [T, Dispatch<SetStateAction<T>>] {
  const [entry, setEntry] = useState(() => ({
    storageKey,
    state: readState(storageKey, storageType, initialState, restore),
  }));

  let current = entry;
  if (entry.storageKey !== storageKey) {
    current = {
      storageKey,
      state: readState(storageKey, storageType, initialState, restore),
    };
    setEntry(current);
  }

  const setState = useCallback<Dispatch<SetStateAction<T>>>(
    (action) =>
      setEntry((previous) => ({
        storageKey: previous.storageKey,
        state:
          typeof action === 'function'
            ? (action as (previous: T) => T)(previous.state)
            : action,
      })),
    [],
  );

  useEffect(() => {
    const storage = webStorage(storageType);
    if (!current.storageKey || !storage) return;
    try {
      storage.setItem(current.storageKey, JSON.stringify(current.state));
    } catch {
      // Full or blocked storage only costs the memory, not the state.
    }
  }, [current.storageKey, current.state, storageType]);

  return [current.state, setState];
}
